import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: 'marketing', label: 'Comunicazioni informative e commerciali (newsletter)' },
  { key: 'profilazione', label: 'Profilazione per offerte personalizzate' },
];

export default function Consensi() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [state, setState] = useState('checking'); // checking | invalid | form | done
  const [info, setInfo] = useState(null);
  const [choices, setChoices] = useState({ marketing: false, profilazione: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    axios.get(`${API}/clients/consent/${encodeURIComponent(token)}`)
      .then((res) => {
        setInfo(res.data);
        setChoices({
          marketing: !!res.data.current?.marketing,
          profilazione: !!res.data.current?.profilazione,
        });
        setState('form');
      })
      .catch(() => setState('invalid'));
  }, [token]);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/clients/consent/${encodeURIComponent(token)}`, choices);
      setState('done');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore. Il link potrebbe essere scaduto.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-2xl border-slate-200">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <ShieldCheck className="w-5 h-5 text-emerald-600" /> Consensi privacy
          </CardTitle>
          <CardDescription>JV SERVICE SRLS</CardDescription>
        </CardHeader>
        <CardContent>
          {state === 'checking' && (
            <div className="flex items-center justify-center gap-2 text-slate-500 py-10">
              <Loader2 className="w-5 h-5 animate-spin" /> Verifica del link…
            </div>
          )}

          {state === 'invalid' && (
            <div className="text-center py-8 space-y-3">
              <XCircle className="w-10 h-10 text-red-500 mx-auto" />
              <p className="text-slate-700">Link non valido o scaduto.</p>
              <p className="text-sm text-slate-500">Contatta JV Service per riceverne uno nuovo.</p>
            </div>
          )}

          {state === 'form' && info && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Gentile <strong>{info.client_name}</strong>, di seguito l'informativa sul trattamento
                dei tuoi dati. In fondo puoi indicare le tue preferenze sui trattamenti facoltativi.
              </p>

              <pre className="text-[11px] leading-relaxed bg-slate-50 border rounded p-3 max-h-72 overflow-y-auto whitespace-pre-wrap text-slate-600">
                {info.informativa?.text}
              </pre>

              <div className="rounded-lg border border-slate-200 divide-y">
                <div className="p-3 text-xs text-slate-500 bg-slate-50">
                  La gestione del rapporto/servizio e gli adempimenti di legge non richiedono consenso
                  e proseguono comunque.
                </div>
                {FIELDS.map((f) => (
                  <div key={f.key} className="p-3 flex items-center justify-between gap-3">
                    <span className="text-sm text-slate-700">{f.label}</span>
                    <Switch
                      checked={choices[f.key]}
                      onCheckedChange={(v) => setChoices((x) => ({ ...x, [f.key]: v }))}
                    />
                  </div>
                ))}
              </div>

              <p className="text-[11px] text-slate-400">
                Informativa versione {info.informativa?.version}. Registriamo data, ora e questo
                invio come prova del consenso. Puoi revocare i consensi in qualsiasi momento
                scrivendo a assicurazioni@jvservice.it.
              </p>

              <Button className="w-full" onClick={submit} disabled={saving}>
                {saving ? 'Invio…' : 'Conferma le mie scelte'}
              </Button>
            </div>
          )}

          {state === 'done' && (
            <div className="text-center py-8 space-y-3">
              <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto" />
              <p className="text-slate-700">Grazie, le tue preferenze sono state registrate.</p>
              <p className="text-sm text-slate-500">Puoi chiudere questa pagina.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
